// Product categories, in one place.
//
// The list was duplicated across the shop filter, two admin selects, two tag
// colour maps and a TS union — six copies that had to be edited together, and
// weren't: the app's shop offered an "Exotic" filter for a category the column
// never accepted, so it could only ever return an empty list.
//
// The column itself (an ENUM) is defined in server/config/migrate.js, in the
// `alterations` entry that re-applies it on every boot. Adding a category means
// changing that line AND this one.
// What KIND of thing it is. Where it sits is FOOD_GROUPS, below.
//
// 'fruit' and 'frozen' used to live here too and are deliberately gone: they
// are shelves, not kinds. Having both meant an admin said "Frozen" twice, and
// the shop put a Frozen chip under the Frozen shelf, narrowing nothing. The
// ENUM still accepts them so old rows load, but nothing offers them and the
// migration clears the ones that were stored.
export const PRODUCT_CATEGORIES = [
  { value: 'leafy',    label: 'Leafy',    emoji: '🥬', color: 'green' },
  { value: 'regular',  label: 'Regular',  emoji: '🥕', color: 'blue' },
  { value: 'seasonal', label: 'Seasonal', emoji: '🍂', color: 'orange' },
  { value: 'exotic',   label: 'Exotic',   emoji: '✨', color: 'purple' },
] as const;

export type ProductCategory = typeof PRODUCT_CATEGORIES[number]['value'];

// The shelf a product sits on, above its category.
//
// Deliberately not derived from the category: seasonal and exotic cut across
// both halves of the shop. A mango is a seasonal fruit and a dragon fruit an
// exotic one, so a lookup from category to group would file them under
// Vegetables for ever.
//
// THESE ARE DATA NOW, not a list in this file. They live in the food_groups
// table with an admin screen, because a new shelf used to cost a migration, a
// web deploy and an app-store release — for one row. What stays here is the
// fallback: the three the shop shipped with, used until the fetch lands and
// kept if it fails, because a shop with no tabs looks broken in a way a
// slightly stale shop does not.
export interface FoodGroupRow {
  value: string;
  label: string;
  emoji?: string | null;
  color?: string | null;
}

export const FALLBACK_FOOD_GROUPS: FoodGroupRow[] = [
  { value: 'veg',    label: 'Vegetables', emoji: '🥬', color: 'green' },
  { value: 'fruit',  label: 'Fruits',     emoji: '🍎', color: 'volcano' },
  { value: 'frozen', label: 'Frozen',     emoji: '🧊', color: 'cyan' },
];

/**
 * Kept as a mutable module-level list rather than React state.
 *
 * Three unrelated screens and two plain helper functions need it, and
 * groupLabel/groupColor are called from table renderers that have no hook
 * context. One fetch fills it; everything reads it synchronously, exactly as
 * they did when it was a constant.
 */
let liveGroups: FoodGroupRow[] = FALLBACK_FOOD_GROUPS;

/** Replace the list once the server has answered. */
export function setFoodGroups(rows: FoodGroupRow[]): void {
  if (Array.isArray(rows) && rows.length > 0) liveGroups = rows;
}

export function foodGroups(): FoodGroupRow[] {
  return liveGroups;
}

/**
 * A shelf slug is a free-form string now, so this is no longer a union.
 *
 * That is the trade for configurability: a typo'd shelf used to fail the
 * typecheck and now fails at runtime, which is why the server validates every
 * write against the table and the admin picks from a list rather than typing.
 */
export type FoodGroup = string;

/** Top row of the shop filter: the groups, with "All" first. */
export function groupFilters(): { value: string; label: string }[] {
  return [
    { value: 'all', label: 'All' },
    ...liveGroups.map(g => ({ value: g.value, label: `${g.emoji || ''} ${g.label}`.trim() })),
  ];
}

/** For an admin <Select>: no emoji cluttering the form. */
export function groupOptions(): { value: string; label: string }[] {
  return liveGroups.map(g => ({ value: g.value, label: g.label }));
}

export function groupLabel(value: string): string {
  return liveGroups.find(g => g.value === value)?.label ?? value;
}

export function groupColor(value: string): string {
  return liveGroups.find(g => g.value === value)?.color ?? 'default';
}

// CATEGORY_FILTERS is gone: customers choose a shelf now, not a kind.
// PRODUCT_CATEGORIES stays because the admin still files by it.

/** For an admin <Select>: no "All", and no emoji cluttering the form. */
export const CATEGORY_OPTIONS = PRODUCT_CATEGORIES.map(c => ({ value: c.value, label: c.label }));

/** Tag colour for a stored value, falling back for anything unrecognised. */
export function categoryColor(value: string): string {
  return PRODUCT_CATEGORIES.find(c => c.value === value)?.color ?? 'default';
}
